import { Button, Group, NumberInput, Select, Stack, Text, TextInput } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { useEditPile, usePileDensity } from "../../api/generated/stockvol";
import type { PileHistory } from "../../api/generated/model";
import { act, confirm } from "../../lib/actions";

/** Name a pile, set its product and blend, mark it dormant or cleared; override its density. */
export function PileEditor({ pile }: { pile: PileHistory }) {
  const qc = useQueryClient();
  const edit = useEditPile();
  const density = usePileDensity();
  const [label, setLabel] = useState(pile.label ?? "");
  const [product, setProduct] = useState(pile.product ?? "");
  const [blend, setBlend] = useState(pile.blend ?? "");
  const [status, setStatus] = useState<string | null>(pile.status);
  const [rho, setRho] = useState<number | string>("");
  const refresh = ["piles", "runs", "pile"];
  return (
    <Stack gap="xs" data-testid="pile-editor">
      <Text size="sm" fw={600}>
        Edit
      </Text>
      <Group grow>
        <TextInput size="xs" label="Name" value={label} onChange={(e) => setLabel(e.currentTarget.value)} />
        <TextInput size="xs" label="Product" value={product} onChange={(e) => setProduct(e.currentTarget.value)} />
        <TextInput size="xs" label="Blend" value={blend} onChange={(e) => setBlend(e.currentTarget.value)} />
        <Select
          size="xs"
          label="Status"
          data={["active", "dormant", "cleared"]}
          value={status}
          onChange={setStatus}
        />
      </Group>
      <Group>
        <Button
          size="xs"
          loading={edit.isPending}
          onClick={() =>
            void act(
              () =>
                edit.mutateAsync({
                  code: pile.code,
                  data: {
                    label: label || null,
                    product: product || null,
                    blend: blend || null,
                    ...(status ? { status: status as "active" | "dormant" | "cleared" } : {}),
                  },
                }),
              { done: `${pile.code} saved`, qc, refresh },
            )
          }
        >
          Save
        </Button>
      </Group>
      <Group align="flex-end">
        <NumberInput
          size="xs"
          label="Density override (t/m³)"
          value={rho}
          onChange={setRho}
          min={0.31}
          max={2.99}
          step={0.01}
          decimalScale={3}
          w={180}
        />
        <Button
          size="xs"
          variant="light"
          disabled={rho === ""}
          loading={density.isPending}
          onClick={() =>
            confirm(
              "Override the density",
              `${pile.code} is weighed at ${rho} t/m³ from now on, its latest tonnes included.`,
              () =>
                void act(() => density.mutateAsync({ code: pile.code, data: { rho_t_m3: Number(rho) } }), {
                  done: "Density set",
                  qc,
                  refresh,
                }),
            )
          }
        >
          Set
        </Button>
        <Button
          size="xs"
          variant="subtle"
          loading={density.isPending}
          onClick={() =>
            confirm("Clear the override", `${pile.code} goes back to the measured density.`, () =>
              void act(() => density.mutateAsync({ code: pile.code, data: { rho_t_m3: null } }), {
                done: "Override cleared",
                qc,
                refresh,
              }),
            )
          }
        >
          Clear
        </Button>
      </Group>
    </Stack>
  );
}
